import logger from "@/lib/logger";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import {
    addPageActivityChangeListener,
    canPerformBackgroundRefresh,
    isDocumentVisible,
    isNavigatorOnline,
} from "@/main/common/PageActivity";

type ApiError = {
    response?: {
        status?: number;
        data?: unknown;
    };
    status?: number;
};

type PageViewTrackerOptions = {
    delayMs?: number;
    retryDelayMs?: number;
    maxRetryAttempts?: number;
    requireVisible?: boolean;
    requireOnline?: boolean;
    errorMessage: string;
    context?: Record<string, unknown>;
    trackView: () => Promise<unknown>;
};

export class PageViewTracker {
    private readonly delayMs: number;
    private readonly retryDelayMs: number;
    private readonly maxRetryAttempts: number;
    private readonly requireVisible: boolean;
    private readonly requireOnline: boolean;
    private readonly errorMessage: string;
    private readonly context?: Record<string, unknown>;
    private readonly trackView: () => Promise<unknown>;

    private viewTimeoutId: number | null = null;
    private retryTimeoutId: number | null = null;
    private removePageActivityListener: (() => void) | null = null;
    private pendingDelayMs: number | null = null;
    private hasRecordedView = false;
    private stopped = false;
    private retryAttempts = 0;

    constructor(options: PageViewTrackerOptions) {
        this.delayMs = options.delayMs ?? 5_000;
        this.retryDelayMs = options.retryDelayMs ?? 10_000;
        this.maxRetryAttempts = options.maxRetryAttempts ?? 3;
        this.requireVisible = options.requireVisible ?? true;
        this.requireOnline = options.requireOnline ?? true;
        this.errorMessage = options.errorMessage;
        this.context = options.context;
        this.trackView = options.trackView;
    }

    /**
     * Starts the attempt schedule.
     *
     * <p><b>Nothing here decides whether a read "counts".</b> Until 2026-08-20 this returned early for
     * any client `isLikelyAutomatedClient()` matched, which made the browser the place where a read was
     * silently discarded — invisible to the server, unrecoverable afterwards, and impossible to test,
     * since the same predicate matches `navigator.webdriver` and therefore every Playwright run. The
     * server now classifies each recorded read (`discussion_views.traffic_type`) instead, so a machine
     * read is a labelled row rather than an absent one, and a VPN or corporate-proxy reader keeps the
     * read that gate used to take. Classify, never drop.
     *
     * <p>See designs/08-audits-and-process/2026-08-20-discussion-view-collapse-incident.md §4.1. Do not
     * reintroduce a client-side skip: the decision belongs where it can be recorded and revisited.
     */
    public startViewTracking(): void {
        this.stopped = false;
        this.retryAttempts = 0;
        this.clearTimeouts();
        this.teardownPageActivityListener();
        this.removePageActivityListener = addPageActivityChangeListener(() => {
            this.resumePendingAttemptIfEligible();
        });

        this.scheduleAttempt(this.delayMs);
    }

    public stopViewTracking(): void {
        this.stopped = true;
        this.clearTimeouts();
        this.pendingDelayMs = null;
        this.teardownPageActivityListener();
    }

    private clearTimeouts(): void {
        if (this.viewTimeoutId !== null) {
            window.clearTimeout(this.viewTimeoutId);
            this.viewTimeoutId = null;
        }

        if (this.retryTimeoutId !== null) {
            window.clearTimeout(this.retryTimeoutId);
            this.retryTimeoutId = null;
        }
    }

    private teardownPageActivityListener(): void {
        this.removePageActivityListener?.();
        this.removePageActivityListener = null;
    }

    private isEligibleToTrack(): boolean {
        if (this.requireVisible && this.requireOnline) {
            return canPerformBackgroundRefresh();
        }

        if (this.requireVisible) {
            return isDocumentVisible();
        }

        if (this.requireOnline) {
            return isNavigatorOnline();
        }

        return true;
    }

    private scheduleAttempt(delayMs: number): void {
        if (this.hasRecordedView || this.stopped) {
            return;
        }

        if (!this.isEligibleToTrack()) {
            this.pendingDelayMs = delayMs;
            return;
        }

        this.pendingDelayMs = null;
        this.viewTimeoutId = window.setTimeout(() => {
            this.viewTimeoutId = null;
            this.recordView();
        }, delayMs);
    }

    private scheduleRetry(delayMs: number): void {
        if (this.hasRecordedView || this.stopped) {
            return;
        }

        this.retryTimeoutId = window.setTimeout(() => {
            this.retryTimeoutId = null;

            if (!this.isEligibleToTrack()) {
                this.pendingDelayMs = 0;
                return;
            }

            this.recordView();
        }, delayMs);
    }

    private resumePendingAttemptIfEligible(): void {
        if (this.stopped || this.hasRecordedView || this.pendingDelayMs == null || !this.isEligibleToTrack()) {
            return;
        }

        const delayMs = this.pendingDelayMs;
        this.pendingDelayMs = null;
        this.scheduleAttempt(delayMs);
    }

    private recordView(): void {
        if (this.hasRecordedView || this.stopped) {
            return;
        }

        if (!this.isEligibleToTrack()) {
            this.pendingDelayMs = 0;
            return;
        }

        this.trackView()
            .then(() => {
                this.hasRecordedView = true;
                this.pendingDelayMs = null;
                this.retryTimeoutId = null;
            })
            .catch((error) => {
                if (isHandledAuthSessionError(error)) {
                    return;
                }

                const apiError = error as ApiError;
                const status = apiError.response?.status ?? apiError.status;
                // Analytics-grade tracking: only genuine server faults (5xx) are actionable. Expected
                // outcomes for anonymous/email-link/throttled/offline clients (auth, CSRF, throttle,
                // network) are noise — log them at debug (not forwarded to the backend) instead of error.
                const isServerFault = status != null && status >= 500;
                const logFn = isServerFault ? logger.error : logger.debug;
                logFn(this.errorMessage, {
                    ...(this.context ?? {}),
                    status,
                    responseData: apiError.response?.data,
                    error,
                });

                if (!this.shouldRetry(error)) {
                    return;
                }

                if (this.retryAttempts >= this.maxRetryAttempts) {
                    return;
                }

                this.retryAttempts += 1;

                this.scheduleRetry(this.retryDelayMs);
            });
    }

    private shouldRetry(error: unknown): boolean {
        const apiError = error as ApiError;
        const status = apiError.response?.status ?? apiError.status;

        if (status == null) {
            return true;
        }

        return status >= 500 || status === 408 || status === 429;
    }
}
